/**
 * t446u: the Section field in Create a channel, and New section (name only) on top of the form.
 * Computer: a select with "New section…" at the end, which opens a small window over the form.
 * Phone: a row that opens a Section sheet over the form, with "New section…" opening one more sheet on top (swipe to
 * close, no ✕), like the app. Rules and words come from @tradeoutlet/shared (channelSections).
 */
import { useEffect, useState } from 'react';
import { Check, ChevronRight, Plus } from 'lucide-react';
import { NEW_SECTION_LABEL, sectionChoiceLabel, sectionNameError, sectionOptions, type ChatSection } from '@tradeoutlet/shared';
import { api, errorMessage } from '@/lib/api';
import { useWorkspaceId } from '@/lib/hooks';
import { qk, queryClient } from '@/lib/queryClient';
import { Button, ErrorBox, Field, Input, Modal, Select } from '@/components/ui';
import { BottomSheet } from '@/mobile/BottomSheet';

const NEW = '__new';

export function SectionField({ sections, value, onChange, phone }: { sections: ChatSection[]; value: string | null; onChange: (id: string | null) => void; phone: boolean }) {
  const [listOpen, setListOpen] = useState(false);
  const [adding, setAdding] = useState(false);
  const options = sectionOptions(sections);
  const created = (id: string) => { onChange(id); setAdding(false); setListOpen(false); };
  return (
    <div data-testid="new-channel-section">
      {phone ? (
        <>
          <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600 dark:text-slate-400">Section</span>
          <button type="button" onClick={() => setListOpen(true)} data-testid="new-channel-section-row" aria-haspopup="dialog"
            className="flex h-11 w-full items-center gap-2 rounded-md border border-slate-300 px-3 text-left text-base dark:border-slate-700">
            <span className="min-w-0 flex-1 truncate" data-testid="new-channel-section-value">{sectionChoiceLabel(sections, value)}</span>
            <ChevronRight className="h-4 w-4 shrink-0 text-slate-400" aria-hidden />
          </button>
          <BottomSheet open={listOpen} onClose={() => setListOpen(false)} title="Section" testId="new-channel-section-sheet">
            <ul className="px-2 pb-2" role="listbox" aria-label="Section">
              {options.map(o => (
                <li key={o.id ?? 'none'}>
                  <button type="button" role="option" aria-selected={value === o.id} onClick={() => { onChange(o.id); setListOpen(false); }}
                    className="flex min-h-[44px] w-full items-center gap-2 rounded-md px-3 text-left text-[15px] active:bg-slate-100 dark:active:bg-slate-800" data-testid="section-option">
                    <span className="min-w-0 flex-1 truncate">{o.label}</span>
                    {value === o.id && <Check className="h-4 w-4 shrink-0" aria-hidden />}
                  </button>
                </li>
              ))}
              <li>
                <button type="button" onClick={() => setAdding(true)} data-testid="section-option-new"
                  className="flex min-h-[44px] w-full items-center gap-2 rounded-md px-3 text-left text-[15px] text-slate-700 active:bg-slate-100 dark:text-slate-200 dark:active:bg-slate-800">
                  <Plus className="h-4 w-4 shrink-0" aria-hidden />{NEW_SECTION_LABEL}
                </button>
              </li>
            </ul>
          </BottomSheet>
        </>
      ) : (
        <Field label="Section">
          <Select value={value ?? ''} onChange={e => { const v = e.target.value; if (v === NEW) setAdding(true); else onChange(v || null); }} data-testid="new-channel-section-select" aria-label="Section">
            {options.map(o => <option key={o.id ?? 'none'} value={o.id ?? ''}>{o.label}</option>)}
            <option value={NEW}>{NEW_SECTION_LABEL}</option>
          </Select>
        </Field>
      )}
      <NewSectionDialog open={adding} onClose={() => setAdding(false)} sections={sections} phone={phone} onCreated={created} />
    </div>
  );
}

/** New section, name only, at the top level. Made straight away (it is empty until a channel goes in), then chosen. */
export function NewSectionDialog({ open, onClose, sections, phone, onCreated }: { open: boolean; onClose: () => void; sections: ChatSection[]; phone: boolean; onCreated: (id: string) => void }) {
  const ws = useWorkspaceId();
  const [name, setName] = useState('');
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setName(''); setError(null); } }, [open]);
  const add = async () => {
    const problem = sectionNameError(name, sections);
    if (problem) { setError(problem); return; }
    if (!ws) return;
    setBusy(true);
    try {
      const s = await api.createChatSection(ws, { name: name.trim() });
      queryClient.setQueryData(qk.chatSections(ws), (d: { sections: ChatSection[] } | undefined) => (d ? { ...d, sections: [...d.sections, s] } : d));
      void queryClient.invalidateQueries({ queryKey: qk.chatSections(ws) });
      onCreated(s.id);
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  };
  const addBtn = <Button className={phone ? 'w-full' : undefined} onClick={() => void add()} loading={busy} disabled={!name.trim()} data-testid="new-section-add">Add</Button>;
  const body = (
    <form className={phone ? 'space-y-3 px-4 pb-2' : 'space-y-3'} onSubmit={e => { e.preventDefault(); void add(); }}>
      <Field label="Name"><Input autoFocus value={name} maxLength={80} onChange={e => { setName(e.target.value); setError(null); }} placeholder="e.g. Warehouse" data-testid="new-section-name" /></Field>
      <ErrorBox error={error} />
    </form>
  );
  return phone
    ? <BottomSheet open={open} onClose={onClose} title="New section" testId="new-section-sheet" footer={addBtn}>{body}</BottomSheet>
    : <Modal open={open} onClose={onClose} title="New section" footer={<><Button variant="ghost" onClick={onClose}>Cancel</Button>{addBtn}</>}>{body}</Modal>;
}
